/**
 * ActivityQueueManager
 * 
 * Manages a FIFO queue of rack activities with intelligent merging logic.
 * When multiple events occur for the same rack, they are merged into a single
 * queue item to avoid redundant rack switching.
 * 
 * Features:
 * - FIFO queue with merge logic for same rack
 * - Configurable dwell time per rack
 * - Queue size limits
 * - Statistics tracking
 * - Event deduplication
 * 
 * @author BizClick WMS
 * @version 1.26 - Fixed rack number extraction from BinCode
 */

import { LocationActivityEvent } from '../../../../types/locationActivity';
import {
  QueueItem,
  QueueStats,
  QueueConfig,
  QueueOperationResult,
  DEFAULT_QUEUE_CONFIG
} from './types';

export class ActivityQueueManager {
  private queue: QueueItem[] = [];
  private config: QueueConfig;
  private stats = {
    totalEnqueued: 0,
    totalDequeued: 0,
    totalMerged: 0,
    totalDropped: 0
  };

  /**
   * Create a new ActivityQueueManager
   * @param config Optional configuration (uses defaults if not provided)
   */
  constructor(config?: Partial<QueueConfig>) {
    this.config = { ...DEFAULT_QUEUE_CONFIG, ...config };
  }

  /**
   * Enqueue a new activity event
   * If the rack already exists in queue, merge the event
   * @param event The activity event to enqueue
   * @returns Operation result
   */
  public enqueue(event: LocationActivityEvent): QueueOperationResult {
    try {
      // Extract rack number from BinCode
      // BinCode format: [Rack][Vertical][Shelf] e.g., "011201" = Rack "01", Vertical "12", Shelf "01"
      // We try common rack lengths: 2, 3, 4, 6
      const rackNumber = this.extractRackNumberFromBinCode(event.binCode);
      
      // Use warehouse ID from event (backend should populate this correctly)
      // Note: event.rackNumber from backend is incorrectly typed as int and may be warehouse ID
      const warehouseId = event.warehouseId || event.rackNumber.toString();

      console.log(
        `[ActivityQueueManager] Processing event - BinCode: ${event.binCode}, ` +
        `Extracted Rack: ${rackNumber}, WarehouseId: ${warehouseId}`
      );

      // Check if rack already exists in queue
      const existingIndex = this.queue.findIndex(
        item => item.rackNumber === rackNumber && item.warehouseId === warehouseId
      );

      if (existingIndex !== -1 && this.config.mergeSameRack) {
        // Merge: Add event to existing queue item and update timestamp
        this.queue[existingIndex].events.push(event);
        this.queue[existingIndex].lastEventAt = new Date();
        this.stats.totalMerged++;

        console.log(
          `[ActivityQueueManager] Merged event for rack ${rackNumber} ` +
          `(${this.queue[existingIndex].events.length} events total)`
        );

        return {
          success: true,
          message: `Event merged for rack ${rackNumber}`,
          item: this.queue[existingIndex],
          stats: this.getStats()
        };
      }

      // Check queue size limit
      if (this.queue.length >= this.config.maxQueueSize) {
        this.stats.totalDropped++;
        console.warn(
          `[ActivityQueueManager] Queue full (${this.config.maxQueueSize}), ` +
          `dropping event for rack ${rackNumber}`
        );

        return {
          success: false,
          message: `Queue full, event dropped for rack ${rackNumber}`,
          stats: this.getStats()
        };
      }

      // Create new queue item
      const newItem: QueueItem = {
        warehouseId,
        rackNumber,
        events: [event],
        enqueuedAt: new Date(),
        lastEventAt: new Date()
      };

      this.queue.push(newItem);
      this.stats.totalEnqueued++;

      console.log(
        `[ActivityQueueManager] Enqueued rack ${rackNumber} ` +
        `(queue size: ${this.queue.length})`
      );

      return {
        success: true,
        message: `Rack ${rackNumber} enqueued`,
        item: newItem,
        stats: this.getStats()
      };
    } catch (error) {
      console.error('[ActivityQueueManager] Enqueue error:', error);
      return {
        success: false,
        message: `Enqueue failed: ${error}`
      };
    }
  }

  /**
   * Dequeue and return the next rack to display
   * @returns Next queue item or null if queue is empty
   */
  public dequeue(): QueueItem | null {
    if (this.queue.length === 0) {
      return null;
    }

    const item = this.queue.shift()!;
    this.stats.totalDequeued++;

    console.log(
      `[ActivityQueueManager] Dequeued rack ${item.rackNumber} ` +
      `with ${item.events.length} events (queue size: ${this.queue.length})`
    );

    return item;
  }

  /**
   * Peek at the next rack without removing it
   * @returns Next queue item or null if queue is empty
   */
  public peek(): QueueItem | null {
    return this.queue.length > 0 ? this.queue[0] : null;
  }

  /**
   * Get current queue statistics
   * @returns Queue statistics
   */
  public getStats(): QueueStats {
    const totalEvents = this.queue.reduce((sum, item) => sum + item.events.length, 0);
    const oldestItem = this.queue.length > 0 ? this.queue[0] : null;
    const oldestItemAge = oldestItem
      ? Date.now() - oldestItem.enqueuedAt.getTime()
      : undefined;

    return {
      queueLength: this.queue.length,
      uniqueRacks: this.queue.length, // Each queue item is a unique rack
      totalEvents,
      avgEventsPerRack: this.queue.length > 0 ? totalEvents / this.queue.length : 0,
      oldestItemAge
    };
  }

  /**
   * Get all lifetime statistics
   * @returns Lifetime statistics object
   */
  public getLifetimeStats() {
    return {
      ...this.stats,
      currentQueueSize: this.queue.length
    };
  }

  /**
   * Clear the entire queue
   */
  public clear(): void {
    const clearedCount = this.queue.length;
    this.queue = [];
    console.log(`[ActivityQueueManager] Queue cleared (${clearedCount} items removed)`);
  }

  /**
   * Remove a specific rack from the queue
   * @param rackNumber Rack number to remove
   * @param warehouseId Warehouse identifier
   * @returns True if rack was found and removed
   */
  public removeRack(rackNumber: string, warehouseId: string): boolean {
    const initialLength = this.queue.length;
    this.queue = this.queue.filter(
      item => !(item.rackNumber === rackNumber && item.warehouseId === warehouseId)
    );

    const removed = this.queue.length < initialLength;
    if (removed) {
      console.log(`[ActivityQueueManager] Removed rack ${rackNumber} from queue`);
    }

    return removed;
  }

  /**
   * Get all queue items (for debugging/visualization)
   * @returns Array of all queue items
   */
  public getAllItems(): QueueItem[] {
    return [...this.queue];
  }

  /**
   * Check if queue is empty
   * @returns True if queue is empty
   */
  public isEmpty(): boolean {
    return this.queue.length === 0;
  }

  /**
   * Get queue size
   * @returns Number of items in queue
   */
  public size(): number {
    return this.queue.length;
  }

  /**
   * Update configuration
   * @param config Partial configuration to update
   */
  public updateConfig(config: Partial<QueueConfig>): void {
    this.config = { ...this.config, ...config };
    console.log('[ActivityQueueManager] Configuration updated:', this.config);
  }

  /**
   * Get current configuration
   * @returns Current queue configuration
   */
  public getConfig(): QueueConfig {
    return { ...this.config };
  }

  /**
   * Extract rack number from BinCode
   * Tries common rack lengths: 2, 3, 4, 6 characters
   * Falls back to first 2 characters if no better match found
   * 
   * @param binCode The bin code to parse (e.g., "011201", "030205")
   * @returns Rack number as string
   */
  private extractRackNumberFromBinCode(binCode: string): string {
    if (!binCode || binCode.length < 2) {
      console.warn(`[ActivityQueueManager] Invalid BinCode: ${binCode}`);
      return binCode || 'UNKNOWN';
    }

    // Try common rack lengths in order of likelihood
    const commonRackLengths = [2, 6, 4, 3];
    
    for (const length of commonRackLengths) {
      if (binCode.length >= length) {
        const rack = binCode.substring(0, length);
        // Additional validation: check if remaining length makes sense for vertical+shelf
        const remaining = binCode.length - length;
        // Typical combinations: 2+2, 2+2+2, etc.
        if (remaining % 2 === 0 || remaining % 3 === 0) {
          return rack;
        }
      }
    }

    // Fallback: use first 2 characters
    return binCode.substring(0, 2);
  }

  /**
   * Reset all statistics
   */
  public resetStats(): void {
    this.stats = {
      totalEnqueued: 0,
      totalDequeued: 0,
      totalMerged: 0,
      totalDropped: 0
    };
    console.log('[ActivityQueueManager] Statistics reset');
  }
}

export default ActivityQueueManager;
